/**
 * Marketing → Lists sub-page. Admin can create static lists, view members,
 * bulk-move/remove contacts, import/export CSV. Dynamic lists (all customers,
 * active subscribers, all referrers) are read-only and resolved live server-side.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Search, Download, Upload, Users2, Sparkles, Tag, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function MarketingLists() {
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const [openNew, setOpenNew] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const load = useCallback(async () => {
    const { data } = await api.get("/marketing/lists");
    setLists(data);
    if (selectedList) {
      const still = data.find((l) => l.id === selectedList.id);
      if (still) setSelectedList(still);
    }
  }, [selectedList]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selectedList) { setContacts([]); return; }
    api.get(`/marketing/lists/${selectedList.id}/contacts`, { params: q ? { q } : {} })
      .then((r) => setContacts(r.data || []))
      .catch(() => setContacts([]));
    setSelectedIds(new Set());
  }, [selectedList, q]);

  const toggleId = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const selectAll = () => setSelectedIds(new Set(contacts.filter(c => !c.dynamic).map(c => c.id)));
  const clearSel = () => setSelectedIds(new Set());

  const removeFromList = async () => {
    if (!selectedIds.size || !selectedList || selectedList.kind === "dynamic") return;
    await api.post("/marketing/contacts/bulk-remove", {
      list_id: selectedList.id,
      contact_ids: Array.from(selectedIds),
    });
    toast.success(`Removed ${selectedIds.size} contacts`);
    setSelectedIds(new Set());
    load();
    api.get(`/marketing/lists/${selectedList.id}/contacts`).then((r) => setContacts(r.data || []));
  };

  const deleteList = async () => {
    if (!selectedList || selectedList.kind === "dynamic") return;
    if (!window.confirm(`Delete list "${selectedList.name}"? Contacts remain but leave this list.`)) return;
    await api.delete(`/marketing/lists/${selectedList.id}`);
    toast.success("List deleted");
    setSelectedList(null);
    load();
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[280px,1fr] gap-4">
      {/* Left rail — list index */}
      <div className="space-y-2">
        <Button size="sm" className="w-full" onClick={() => setOpenNew(true)} data-testid="mkt-new-list-btn">
          <Plus className="w-4 h-4 mr-2" /> New list
        </Button>
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="px-3 py-2 border-b border-border text-[10px] overline text-muted-foreground">Your lists</div>
          {lists.length === 0 && <div className="p-5 text-xs text-muted-foreground">No lists yet.</div>}
          {lists.map((l) => (
            <button
              key={l.id}
              onClick={() => setSelectedList(l)}
              className={`w-full text-left px-3 py-2 border-b border-border last:border-0 hover:bg-muted/50 transition-colors ${
                selectedList?.id === l.id ? "bg-primary/5 border-l-2 border-l-primary" : ""
              }`}
              data-testid={`mkt-list-${l.id}`}
            >
              <div className="flex items-center gap-2">
                {l.kind === "dynamic" ? <Sparkles className="w-3.5 h-3.5 text-secondary" /> : <Users2 className="w-3.5 h-3.5 text-muted-foreground" />}
                <div className="text-sm font-medium truncate flex-1">{l.name}</div>
                {l.contact_count != null && <span className="text-[10px] text-muted-foreground">{l.contact_count}</span>}
              </div>
              {l.description && <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{l.description}</div>}
            </button>
          ))}
        </div>
      </div>

      {/* Right — selected list */}
      <div className="space-y-3">
        {!selectedList && (
          <div className="p-12 text-center bg-card border border-dashed border-border rounded-2xl">
            <Users2 className="w-10 h-10 mx-auto text-muted-foreground/40" />
            <div className="mt-3 font-display text-lg">Select a list to view contacts</div>
            <p className="text-sm text-muted-foreground mt-1">Or create a new one from the left.</p>
          </div>
        )}
        {selectedList && (
          <>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-display text-2xl font-light truncate">{selectedList.name}</h3>
                  <span className="text-[10px] overline px-2 py-0.5 rounded-full bg-muted text-muted-foreground capitalize">{selectedList.kind}</span>
                </div>
                {selectedList.description && <p className="text-xs text-muted-foreground mt-0.5">{selectedList.description}</p>}
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/marketing/contacts/export.csv?list_id=${selectedList.id}`, "_blank")}>
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
                </Button>
                {selectedList.kind === "static" && (
                  <ImportCsvButton listId={selectedList.id} onDone={() => api.get(`/marketing/lists/${selectedList.id}/contacts`).then((r) => setContacts(r.data || []))} />
                )}
                {selectedList.kind === "static" && !selectedList.tags?.includes("auto") && (
                  <Button variant="ghost" size="sm" className="text-destructive" onClick={deleteList}>
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete list
                  </Button>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input placeholder="Search contacts…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="mkt-contacts-search" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={selectAll} data-testid="mkt-select-all">Select all</Button>
                {selectedIds.size > 0 && <Button variant="outline" size="sm" onClick={clearSel}>Clear ({selectedIds.size})</Button>}
                {selectedIds.size > 0 && selectedList.kind === "static" && (
                  <Button variant="ghost" size="sm" className="text-destructive" onClick={removeFromList} data-testid="mkt-bulk-remove">
                    <X className="w-3.5 h-3.5 mr-1.5" /> Remove from list
                  </Button>
                )}
              </div>
            </div>

            <div className="bg-card border border-border rounded-2xl overflow-hidden">
              <div className="grid grid-cols-12 px-4 py-2 border-b border-border bg-muted/40 text-[11px] overline">
                <div className="col-span-1"></div>
                <div className="col-span-4">Email</div>
                <div className="col-span-3">Name</div>
                <div className="col-span-3">Tags</div>
                <div className="col-span-1 text-right">Sub</div>
              </div>
              {contacts.length === 0 && (
                <div className="p-10 text-center text-sm text-muted-foreground">
                  {selectedList.kind === "dynamic" ? "No matching contacts yet." : "No contacts in this list. Import a CSV or invite referrers."}
                </div>
              )}
              {contacts.map((c) => (
                <div key={c.id} className="grid grid-cols-12 px-4 py-2 border-b border-border last:border-0 text-sm items-center" data-testid={`mkt-contact-${c.email}`}>
                  <div className="col-span-1">
                    <input
                      type="checkbox"
                      disabled={c.dynamic}
                      checked={selectedIds.has(c.id)}
                      onChange={() => toggleId(c.id)}
                      data-testid={`mkt-contact-check-${c.email}`}
                    />
                  </div>
                  <div className="col-span-4 truncate font-mono text-xs">{c.email}</div>
                  <div className="col-span-3 truncate">{c.name || <span className="text-muted-foreground">—</span>}</div>
                  <div className="col-span-3 flex gap-1 flex-wrap">
                    {(c.tags || []).slice(0, 3).map((t) => (
                      <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary/15 text-secondary">
                        <Tag className="w-2.5 h-2.5 inline mr-0.5" />{t}
                      </span>
                    ))}
                  </div>
                  <div className="col-span-1 text-right">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${c.subscribed !== false ? "bg-primary/10 text-primary" : "bg-destructive/10 text-destructive"}`}>
                      {c.subscribed !== false ? "yes" : "no"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <NewListDialog open={openNew} onOpenChange={setOpenNew} onCreated={() => { setOpenNew(false); load(); toast.success("List created"); }} />
    </div>
  );
}

function NewListDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ name: "", description: "", kind: "static", dynamic_key: "all_customers" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault(); setErr(null); setSaving(true);
    try {
      await api.post("/marketing/lists", form);
      setForm({ name: "", description: "", kind: "static", dynamic_key: "all_customers" });
      onCreated();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display font-light text-2xl">New list</DialogTitle>
          <DialogDescription>Group contacts however you like — send them broadcasts, tag them, export.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs">Name</Label>
            <Input required data-testid="mkt-list-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger className="mt-1.5" data-testid="mkt-list-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="static">Static — manually managed</SelectItem>
                <SelectItem value="dynamic">Dynamic — auto-updated from data</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.kind === "dynamic" && (
            <div>
              <Label className="text-xs">Dynamic source</Label>
              <Select value={form.dynamic_key} onValueChange={(v) => setForm({ ...form, dynamic_key: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_customers">All customers</SelectItem>
                  <SelectItem value="active_subscribers">Active subscribers</SelectItem>
                  <SelectItem value="all_referrers">All active referrers</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {err && <div className="text-sm text-destructive">{err}</div>}
          <DialogFooter>
            <Button type="submit" data-testid="mkt-list-submit" disabled={saving}>{saving ? "Creating…" : "Create list"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ImportCsvButton({ listId, onDone }) {
  const [open, setOpen] = useState(false);
  const [csv, setCsv] = useState("email,name,tags\n");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/marketing/contacts/import-csv", { list_id: listId, csv });
      toast.success(`Imported ${data.imported}, skipped ${data.skipped}`);
      setOpen(false); setCsv("email,name,tags\n"); onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Upload className="w-3.5 h-3.5 mr-1.5" /> Import CSV
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display font-light text-2xl">Import contacts</DialogTitle>
            <DialogDescription>Paste CSV — columns: email (required), name, tags (comma-sep).</DialogDescription>
          </DialogHeader>
          <Textarea rows={10} value={csv} onChange={(e) => setCsv(e.target.value)} className="font-mono text-xs" data-testid="mkt-import-csv" />
          <DialogFooter>
            <Button onClick={submit} disabled={busy} data-testid="mkt-import-submit">{busy ? "Importing…" : "Import"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
